import { createSlice, isAnyOf } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from "uuid";
import {
  getPurchaseDebitNote,
  getPurchaseDebitNoteById,
  getParticularPage,
  getNext,
  getPrevious,
  handleSearch,
  createPurchaseDebitNote,
  getPurchaseDebitNoteDetailsById,
} from "./thunk";
import { NepaliDate } from "@zener/nepali-datepicker-react";
import { dateFormater } from "../../../../../../Utils/dateFormater";

// Helper to create an empty row object
const createEmptyDetailRow = () => ({
  product: null,
  vendor: null,
  closingStock: "",
  description: "",
  actualQuantity: "",
  unit: "",
  altQuantity: "",
  altUnit: null,
  rate: "",
  amount: "",
  requiredDate: dateFormater(new NepaliDate()),
  unique: uuidv4(),
  noEffectInventory: false,
});

const initialState = {
  purchaseDebitNotes: [],
  edit: false,
  purchaseDebitNote: null,
  count: 0,
  pageNo: 1,
  next: null,
  previous: null,
  loading: false,
  loadingCreate: false,
  loadingUpdated: false,
  loadingPurchaseDebitNote: false,
  loadingNext: false,
  saveAndContinue: false,
  purchaseDebitNoteDetails: [createEmptyDetailRow()],
  showCreateModal: false,
  selectedPurchaseInvoice: null,
  selectedPurchaseInvoiceDetails: [],
};

export const purchaseDebitNoteSlice = createSlice({
  name: "purchaseDebitNote",
  initialState,
  reducers: {
    setShowCreateModal: (state, action) => {
      state.showCreateModal = action.payload;
    },
    setSaveAndContinue: (state, { payload }) => {
      state.saveAndContinue = payload;
    },
    goToPage: (state, { payload }) => {
      state.pageNo = payload;
    },
    setPurchaseInvoice: (state, action) => {
      state.selectedPurchaseInvoice = action.payload;
    },
    setSelectedInvoiceDetails: (state, action) => {
      state.selectedPurchaseInvoiceDetails = action.payload.map((value) => ({
        ...value,
        description: value.product?.name || "",
      }));
    },
    setPurchaseDebitNote: (state, action) => {
      state.purchaseDebitNote = action.payload;
    },

    purchaseDebitNoteDetailsUpdated: (state, action) => {
      state.purchaseDebitNoteDetails = action.payload.map((value) => ({
        ...value,
        description: value.description || value.product?.name,
        unique: uuidv4(),
      }));
    },
    clearSelectedOrderData: (state) => {
      state.selectedPurchaseInvoice = null;
      state.selectedPurchaseInvoiceDetails = [];
    },
    clearPurchaseDebitNoteDetails: (state) => {
      state.purchaseDebitNoteDetails = [createEmptyDetailRow()];
    },
    clearAllPurchaseDebitNote: (state) => {
      state.edit = false;
      state.loading = false;
      state.loadingUpdated = false;
      state.loadingPurchaseDebitNote = false;
      state.purchaseDebitNote = null;
      state.purchaseDebitNoteDetails = [createEmptyDetailRow()];
      state.selectedPurchaseInvoice = null;
      state.selectedPurchaseInvoiceDetails = [];
    },
    resetLoadingStates: (state) => {
      state.loading = false;
      state.loadingCreate = false;
      state.loadingUpdated = false;
      state.loadingPurchaseDebitNote = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPurchaseDebitNoteById.pending, (state) => {
      state.loading = true;
      state.purchaseDebitNote = null;
    });
    builder.addCase(getPurchaseDebitNoteById.fulfilled, (state, action) => {
      state.edit = true;
      state.loading = false;
      state.purchaseDebitNote = action.payload.data;
      state.purchaseDebitNoteDetails = action.payload.data?.drCrNotesDetails?.map((value) => ({
        ...value,
        unique: uuidv4(),
      })) || [createEmptyDetailRow()];
    });
    builder.addCase(getPurchaseDebitNoteById.rejected, (state) => {
      state.edit = false;
      state.loading = false;
      state.purchaseDebitNote = null;
    });

    builder.addCase(getPurchaseDebitNoteDetailsById.pending, (state) => {
      state.loading = true;
      state.purchaseDebitNote = null;
    });
    builder.addCase(getPurchaseDebitNoteDetailsById.fulfilled, (state, action) => {
      state.loading = false;
      state.purchaseDebitNote = action.payload.data;
    });
    builder.addCase(getPurchaseDebitNoteDetailsById.rejected, (state) => {
      state.loading = false;
      state.purchaseDebitNote = null;
    });

    builder.addMatcher(
      isAnyOf(
        getPurchaseDebitNote.pending,
        getParticularPage.pending,
        getPrevious.pending,
        getNext.pending,
        handleSearch.pending
      ),
      (state) => {
        state.loadingPurchaseDebitNote = true;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getPurchaseDebitNote.fulfilled,
        getParticularPage.fulfilled,
        getPrevious.fulfilled,
        getNext.fulfilled,
        handleSearch.fulfilled
      ),
      (state, action) => {
        state.loadingPurchaseDebitNote = false;
        state.purchaseDebitNotes = action.payload.data;
        state.count = action.payload.totalCount;
        state.previous = action.payload.previous;
        state.next = action.payload.next;
      }
    );

    builder.addMatcher(
      isAnyOf(
        getPurchaseDebitNote.rejected,
        getParticularPage.rejected,
        getPrevious.rejected,
        getNext.rejected,
        handleSearch.rejected
      ),
      (state) => {
        state.loadingPurchaseDebitNote = false;
      }
    );
  },
});

export const {
  setShowCreateModal,
  setSaveAndContinue,
  goToPage,
  setPurchaseInvoice,
  setSelectedInvoiceDetails,
  setPurchaseDebitNote,

  purchaseDebitNoteDetailsUpdated,
  clearSelectedOrderData,
  clearPurchaseDebitNoteDetails,
  clearAllPurchaseDebitNote,

  resetLoadingStates,
} = purchaseDebitNoteSlice.actions;

export default purchaseDebitNoteSlice.reducer;
